import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip,
} from '@mui/material';
import PayrollVariancePanel from './PayrollVariancePanel';
import { PayrollService } from '../../services/payroll.service';
import { IPayrollRunApproval } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { helpWhy } from '../../help/why';
import { formatMoney } from '../../utils/format';
import { formatDateTime } from '../../utils/dateUtils';

interface Props {
    institutionId: string;
    year: number;
    month: number;
    /** Changes whenever the run is reloaded, so the banner refetches. */
    refreshKey?: unknown;
}

/**
 * B-21: approval state of the selected payroll run. The bank-advice and MFS files stay blocked until
 * the run is approved (by someone other than who finalized or last edited it) and unchanged since.
 */
const PayrollApprovalBanner: React.FC<Props> = ({ institutionId, year, month, refreshKey }) => {
    const { showApiError, showSuccess } = useToast();
    const canApprove = usePermission(PERMISSIONS.PAYROLL_APPROVE);
    const [status, setStatus] = useState<IPayrollRunApproval | null>(null);
    const [busy, setBusy] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [reason, setReason] = useState('');

    const load = useCallback(async () => {
        try {
            setStatus(await PayrollService.getRunApproval(year, month, institutionId));
        } catch {
            setStatus(null);
        }
    }, [institutionId, year, month]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // Nothing to approve on an empty run: no banner.
    if (!status || status.currentHeadcount === 0) return null;

    const handleApprove = async () => {
        setBusy(true);
        try {
            setStatus(await PayrollService.approvePayrollRun(year, month, institutionId));
            showSuccess('Payroll run approved');
        } catch (error) {
            showApiError(error, 'Failed to approve the payroll run');
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async () => {
        setBusy(true);
        try {
            setStatus(await PayrollService.rejectPayrollRun(year, month, reason.trim(), institutionId));
            setRejectOpen(false);
            setReason('');
            showSuccess('Payroll run rejected');
        } catch (error) {
            showApiError(error, 'Failed to reject the payroll run');
        } finally {
            setBusy(false);
        }
    };

    const workers = (n: number | null | undefined) => `${n ?? 0} ${n === 1 ? 'worker' : 'workers'}`;
    const current = `${formatMoney(status.currentNetTotal, 'BDT')} net for ${workers(status.currentHeadcount)}`;
    const approvedFigures = `${formatMoney(status.approvedNetTotal, 'BDT')} net for ${workers(status.approvedHeadcount)}`;
    const who = status.actedByName ?? 'an approver';
    const when = status.actedAt ? formatDateTime(status.actedAt) : '';

    let severity: 'success' | 'warning' | 'error' | 'info' = 'info';
    let text = `Not approved: ${current}. The bank and MFS files cannot be downloaded until a payroll approver (finance controller or MD) approves this run.`;
    if (status.state === 'APPROVED' && status.changedSinceApproval) {
        severity = 'warning';
        text = `Changed after approval. Approved: ${approvedFigures}; now: ${current}. Re-approve this run before downloading the bank or MFS file.`;
    } else if (status.state === 'APPROVED') {
        severity = 'success';
        text = `Approved: ${approvedFigures}, by ${who} on ${when}`;
    } else if (status.state === 'REJECTED') {
        severity = 'error';
        text = `Rejected by ${who}${when ? ` on ${when}` : ''}: ${status.reason ?? ''}`;
    }

    const hasDrafts = (status.currentDraftCount ?? 0) > 0;
    const approveBlockedReason = status.callerIsMaker
        ? 'You finalized or last edited this run, so someone else must approve it'
        : hasDrafts
            ? `${status.currentDraftCount} payroll row(s) are still in draft. Finalize every row before approval.`
            : undefined;

    const showButtons = canApprove && status.currentHeadcount > 0
        && !(status.state === 'APPROVED' && !status.changedSinceApproval);

    return (
        <>
            <Alert
                severity={severity}
                sx={{ mb: 1 }}
                data-testid="payroll-approval-banner"
                action={showButtons ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Tooltip title={approveBlockedReason ?? ''}>
                            <span>
                                <Button data-help="payroll.approval.approve" {...helpWhy(busy ? { kind: 'status', message: 'Another action is in progress.' } : approveBlockedReason ? { kind: 'status', message: approveBlockedReason } : null)} size="small" variant="contained" onClick={handleApprove}
                                        disabled={busy || status.callerIsMaker || hasDrafts}>
                                    Approve
                                </Button>
                            </span>
                        </Tooltip>
                        <Button data-help="payroll.approval.reject" size="small" color="inherit" onClick={() => setRejectOpen(true)} disabled={busy}>
                            Reject
                        </Button>
                    </Box>
                ) : undefined}
            >
                {text}
                {showButtons && hasDrafts && !status.callerIsMaker && (
                    <Box component="span" sx={{ display: 'block' }} data-testid="payroll-approve-blocked">
                        {approveBlockedReason}
                    </Box>
                )}
            </Alert>
            <PayrollVariancePanel institutionId={institutionId} year={year} month={month}
                                  refreshKey={`${String(refreshKey)}-${status.state}-${String(status.actedAt)}`} />
            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} PaperProps={{ 'data-help-page': 'payroll.rejectForm.page' } as any} fullWidth maxWidth="sm">
                <DialogTitle>Reject payroll run</DialogTitle>
                <DialogContent data-help-section="payroll.rejectForm.section.details">
                    <TextField
                        data-help="payroll.rejectForm.field.reason" autoFocus fullWidth multiline minRows={2} margin="dense" label="Reason"
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.rejectForm.cancel" onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button data-help="payroll.rejectForm.save" {...helpWhy(busy ? { kind: 'status', message: 'Another action is in progress.' } : reason.trim() === '' ? { kind: 'status', message: 'Enter a reason first.' } : null)} color="error" variant="contained" onClick={handleReject}
                            disabled={busy || reason.trim() === ''}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PayrollApprovalBanner;
